'use client';

import * as SelectPrimitive from '@radix-ui/react-select';
import React from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { Icon } from '../../../core-components';
import type { PropsWithChildren, ReactNode } from 'react';
export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};
export type RadixSelectProps = PropsWithChildren<{
  variant?: 'block' | 'inline';
  ariaLabel?: string;
  ariaLabelValue?: string;
  placeholder?: string;
  hasDarkPlaceholder?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  freeFloating?: boolean;
  showUpDownButtons?: boolean;
  truncatePreview?: boolean;
  displayValue?: ReactNode;
  align?: 'start' | 'center' | 'end';
  withFlexibleWidth?: boolean;
  options?: SelectOption[];
}> & React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>;
export type RadixSelectItemProps = PropsWithChildren<{
  className?: string;
}> & React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>;

/**
 * `Select` provides a customizable dropdown menu for users to select an option. It uses Radix UI primitives for accessibility and flexibility.
 *
 * ## Basic Usage
 *
 * To use the Select component, you can either wrap `SelectItem` components inside it or use the `options` prop for SSR-safe rendering.
 *
 * ### Using options prop (SSR-safe):
 * ```tsx
 * <Select
 *   ariaLabel="Choose an option"
 *   placeholder="Select one"
 *   value={selectedValue}
 *   options={[
 *     { value: 'opt1', label: 'Option 1' },
 *     { value: 'opt2', label: 'Option 2', disabled: true }
 *   ]}
 * />
 * ```
 *
 * ### Using children:
 * ```tsx
 * <Select ariaLabel="Choose an option" placeholder="Select one">
 *   <SelectItem>Option 1</SelectItem>
 *   <SelectItem>Option 2</SelectItem>
 * </Select>
 * ```
 *
 * `SelectItem` represents an individual option within the Select dropdown. It can be customized with a className for specific styling needs.
 *
 * ## Basic Usage of SelectItem
 *
 * `SelectItem` is used within `Select` to represent the options. You can pass children to define the content of each option.
 *
 * ```tsx
 * <Select>
 *   <SelectItem className="custom-select-item-style">Option 1</SelectItem>
 *   <SelectItem>Option 2</SelectItem>
 * </Select>
 * ```
 *
 * ## Disabled SelectItem
 *
 * To disable a specific option, set the `disabled` prop on `SelectItem`.
 *
 * ```tsx
 * <Select>
 *   <SelectItem disabled>Disabled Option</SelectItem>
 *   <SelectItem>Option 2</SelectItem>
 * </Select>
 * ```
 */

export const RadixSelect = React.forwardRef<HTMLButtonElement, RadixSelectProps>(({
  children,
  ariaLabel = 'Auswahl',
  ariaLabelValue = undefined,
  variant = 'block',
  triggerClassName,
  contentClassName,
  placeholder,
  hasDarkPlaceholder = false,
  showUpDownButtons = false,
  freeFloating = false,
  truncatePreview,
  withFlexibleWidth = false,
  align = 'start',
  displayValue,
  options,
  ...props
}, forwardedRef) => {
  // Calculate display value from options if available
  const computedDisplayValue = React.useMemo(() => {
    if (displayValue !== undefined) {
      return displayValue;
    }
    if (options && props.value) {
      const selectedOption = options.find(opt => opt.value === props.value);
      return selectedOption?.label;
    }
    return undefined;
  }, [displayValue, options, props.value]);
  return <SelectPrimitive.Root {...props}>
        <SelectPrimitive.Trigger ref={forwardedRef} className={twJoin(variant == 'block' && twJoin('inline-flex h-[58px] w-full items-center justify-between p-4 text-left', 'rounded-md border border-stroke-secondary-40 bg-surface-white text-base text-marine-high-emphasis outline-none', 'focus:outline-none focus-visible:shadow-focus-state', 'hover:border-stroke-secondary-40', 'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-surface-secondary-7 disabled:text-marine-medium-emphasis', 'data-[invalid]:border-stroke-error-100', hasDarkPlaceholder ? 'data-[placeholder]:text-secondary-marine-80' : 'data-[placeholder]:text-secondary-marine-48', 'data-[state=open]:shadow-md'), variant == 'inline' && 'inline-flex flex-row items-center gap-2', triggerClassName)} aria-label={ariaLabel}>
          <span className={twMerge(truncatePreview && 'truncate min-w-0')}>
            <SelectPrimitive.Value aria-label={ariaLabelValue} placeholder={placeholder}>
              {computedDisplayValue}
            </SelectPrimitive.Value>
          </span>
          <SelectPrimitive.Icon className="text-marine-high-emphasis ml-2">
            <Icon name="nav-arrow-down" size="sm" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content className={twMerge('relative z-[50000] overflow-auto border border-stroke-secondary-40 bg-surface-white', 'max-h-[var(--radix-select-content-available-height)]', !withFlexibleWidth && 'w-[var(--radix-select-trigger-width)]', freeFloating && 'rounded-md', !freeFloating && ['data-[side=top]:rounded-t-md data-[side=top]:border-b-0 data-[side=top]:pb-2', 'data-[side=bottom]:rounded-b-md data-[side=bottom]:border-t-0 data-[side=bottom]:pt-2', 'data-[side=bottom]:translate-y-[-14px] data-[side=top]:translate-y-[12px]'], contentClassName)} position="popper" align={align}>
            {showUpDownButtons && <SelectPrimitive.ScrollUpButton className="SelectScrollButton">
                <Icon name="nav-arrow-up" />
              </SelectPrimitive.ScrollUpButton>}

            <SelectPrimitive.Viewport className="py-2">
              {options ? options.map(option => <RadixSelectItem key={option.value} value={option.value} disabled={option.disabled}>
                      {option.label}
                    </RadixSelectItem>) : children}
            </SelectPrimitive.Viewport>

            {showUpDownButtons && <SelectPrimitive.ScrollDownButton className="SelectScrollButton">
                <Icon name="nav-arrow-down" />
              </SelectPrimitive.ScrollDownButton>}
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>;
});
export const RadixSelectItem = React.forwardRef<HTMLDivElement, RadixSelectItemProps>(({
  children,
  className,
  ...props
}, forwardedRef) => {
  return <SelectPrimitive.Item className={twJoin('flex cursor-pointer select-none items-center px-4 py-3', 'data-[disabled]:pointer-events-none data-[highlighted]:border-stroke-secondary-40 data-[disabled]:text-marine-high-emphasis data-[highlighted]:outline-none', 'focus:bg-surface-secondary-10', 'data-[state=checked]:font-semibold text-marine-high-emphasis', className)} {...props} ref={forwardedRef}>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      {/*
        <SelectPrimitive.ItemIndicator className="absolute left-2 inline-flex items-center justify-center">
          <Icon name="check" />
        </SelectPrimitive.ItemIndicator>
        */}
    </SelectPrimitive.Item>;
});